// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import ReactTooltip from "react-tooltip";
import ResizeObserver from "resize-observer-polyfill";

// event-current-cost imports
import { IProps, IContainer, IState } from "./EventPeople.d";
import { eventPeopleFragments } from "./EventPeople.gql";
import * as s from "./EventPeople.styles";

// components
import { AttendeesPainsRenderer } from "@components/attendees-pains-renderer";
import { AttendeesPains } from "@clockwise/web-commons/src/components/attendees-pains";
import { InfoTip } from "@components/tool-tip";
import { AvatarPerson } from "@components/avatar";

// util
import { getIndividualAttendeeInconvenienceText } from "@clockwise/client-commons/src/util/scheduling-slot";
import { getAttendeesBucketedByResponse } from "@clockwise/client-commons/src/util/attendees";
import { getScoreText, getScoreSuffix, getFocusTimeSuffix } from "@util/event-score-delta.util";
import { roomString } from "@util/event.util";
import { isOnToolTipDebugging } from "@util/experiment.util";

// material-ui
import { NotInterested } from "@material-ui/icons";

interface PersonImpact {
  impacts?: ISchema.ISchedulingAttendanceImpact;
  calendarId: string;
}

///////////////
// Component //
///////////////

class EventPeopleCmpt extends React.Component<IProps, IState> {
  private toolTipId: string;
  private profileCostsContainer?: HTMLDivElement;
  private resizeObserver?: ResizeObserver;

  constructor(props: IProps) {
    super(props);
    this.toolTipId = `EventPeopleToolTip`;
    this.state = {
      loadedPersons: false,
      expanded: false,
      maxRenderedAvatars: 4,
    };
  }

  public componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      delete this.resizeObserver;
      delete this.profileCostsContainer;
    }
  }

  private bindProfileCostsContainer = (element: HTMLDivElement | null) => {
    if (!element) {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        delete this.resizeObserver;
        delete this.profileCostsContainer;
      }

      return;
    }

    this.profileCostsContainer = element;

    this.resizeObserver = new ResizeObserver(this.updateMaxRenderedAvatars);
    this.resizeObserver.observe(element);

    this.updateMaxRenderedAvatars();
  };

  private updateMaxRenderedAvatars = () => {
    if (this.profileCostsContainer) {
      this.setState({
        maxRenderedAvatars: Math.floor(this.profileCostsContainer.offsetWidth / 45),
      });
    }
  };

  // renders the line showing the event's total score
  private renderScore = (score: number | null, numberAttendees: number) => {
    if (score === null) {
      return null;
    }

    const infoTipText = (
      <span>
        Hours of Focus Time this
        <br />
        meeting is interrupting
        <br />
        at its current time
      </span>
    );

    if (score === 0) {
      return (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          No impact on Focus Time
          <InfoTip text={infoTipText} iconStyle={s.tooltipIcon} tipType={"info"} />
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <span style={s.stat}>
          {getScoreText(score)}
          {getScoreSuffix(score)}&nbsp;
        </span>
        {getFocusTimeSuffix(true, numberAttendees)}
        <InfoTip text={infoTipText} iconStyle={s.tooltipIcon} tipType={"info"} />
      </div>
    );
  };

  // helper to render an avatar
  private renderAvatar = (
    org: ISchema.IOrg,
    person: ISchema.IPerson | null,
    tipText: JSX.Element | null,
    calendarId: string,
    responseStatus: ISchema.ResponseStatusEnum | null,
  ) => {
    const avatar = (
      <AvatarPerson
        hideUserBadge={true}
        responseStatus={responseStatus ? responseStatus : undefined}
        org={org}
        person={person}
        orgPerson={null}
        size={34}
        backupCalendarId={calendarId}
        parentName={"EventPeople"}
      />
    );

    if (!tipText) {
      return avatar;
    }

    return (
      <div key={calendarId} style={s.avatarContainer}>
        <InfoTip targetElement={avatar} text={tipText} tipType={"info"} />
      </div>
    );
  };

  // renders a person
  private renderPerson = (
    org: ISchema.IOrg,
    personImpact: PersonImpact,
    person: ISchema.IPerson,
    attendee: ISchema.IAttendee | null,
  ) => {
    const { impacts } = personImpact;

    const name =
      !!person && !!person.profile
        ? `${person.profile.givenName} ${person.profile.familyName}`
        : personImpact.calendarId;

    let tipText: JSX.Element;

    if (impacts) {
      const { focusTimeMinutesCost } = impacts;
      const score = -focusTimeMinutesCost / 60;

      tipText = (
        <span>
          {name}:<br />
          {getScoreText(score)}
          {getScoreSuffix(score)}
        </span>
      );
    } else {
      tipText = (
        <span>
          {name}:<br />
          Focus Time impact not calculated
        </span>
      );
    }

    return this.renderAvatar(
      org,
      person,
      tipText,
      personImpact.calendarId,
      attendee && attendee.responseStatus,
    );
  };

  private renderPainForIndividual = (
    _org: ISchema.IOrg,
    impact: ISchema.ISchedulingAttendanceImpact,
  ) => {
    const pains = getIndividualAttendeeInconvenienceText(impact);

    return <AttendeesPains pains={pains} />;
  };

  private renderPersonRow = (
    org: ISchema.IOrg,
    personImpact: PersonImpact,
    person: ISchema.IPerson,
    attendee: ISchema.IAttendee | null,
  ) => {
    // TODO: Add cost to person row

    const { impacts, calendarId } = personImpact;

    const name =
      !!person && !!person.profile
        ? `${person.profile.givenName} ${person.profile.familyName}`
        : calendarId;

    let tipText: JSX.Element;
    let painPoints: JSX.Element | null = null;

    if (impacts) {
      const { focusTimeMinutesCost } = impacts;
      const score = -focusTimeMinutesCost / 60;

      tipText = (
        <span>
          {name}:<br />
          {getScoreText(score)}
          {getScoreSuffix(score)}
        </span>
      );

      let conflictString = "Conflict";
      if (impacts?.conflictingMeetings?.length) {
        if (impacts.conflictingMeetings.length === 1) {
          conflictString = `Conflict with ${impacts.conflictingMeetings[0]}`;
        } else {
          conflictString = `Conflict with ${impacts.conflictingMeetings.length} meetings`;
        }
      }

      painPoints = (
        <React.Fragment>
          {impacts.hasConflict ? (
            <div style={s.rowAdditionalWarningText}>
              <NotInterested style={s.rowIcon} />
              {conflictString}
            </div>
          ) : null}
          {impacts.unavailabilities.length ? (
            <div style={s.rowAdditionalWarningText}>
              <NotInterested style={s.rowIcon} />
              Unavailable
            </div>
          ) : null}
          {this.renderPainForIndividual(org, impacts)}
        </React.Fragment>
      );
    } else {
      tipText = (
        <span>
          {name}:<br />
          Focus Time impact not calculated
        </span>
      );
    }

    return (
      <div key={calendarId} style={s.row}>
        {this.renderAvatar(org, person, tipText, calendarId, attendee && attendee.responseStatus)}
        <div style={s.rowText}>
          <div style={s.nameText}>{name}</div>
          {attendee && attendee.organizer ? <div style={s.rowAdditionalText}>Organizer</div> : null}
          <div style={{ paddingTop: 4, paddingRight: 4 }}>{painPoints}</div>
        </div>
      </div>
    );
  };

  private renderPain = (org: ISchema.IOrg, persons: ISchema.IPerson[]) => {
    if (
      !org ||
      org.computeCurrentSlotImpactErrorable.__typename === "GraphEntityError" ||
      this.props.hidePains
    ) {
      return null;
    }

    const currentSlot = org.computeCurrentSlotImpactErrorable.currentSlot;

    if (!isOnToolTipDebugging(org)) {
      return <AttendeesPainsRenderer schedulingSlot={currentSlot} persons={persons} />;
    }

    return (
      <InfoTip
        targetElement={<AttendeesPainsRenderer schedulingSlot={currentSlot} persons={persons} />}
        text={<div>{JSON.stringify(currentSlot)}</div>}
        tipType={"info"}
      />
    );
  };

  // put it all together
  public render() {
    const { org, event } = this.props;
    const { maxRenderedAvatars } = this.state;

    const containerStyle = { transition: "max-height .5s" };

    let score: number | null = null;

    let personImpacts: PersonImpact[];

    // on errors, we will generate the view via the event attendees
    if (
      !org ||
      !org.computeCurrentSlotImpactErrorable ||
      org.computeCurrentSlotImpactErrorable.__typename === "GraphEntityError" ||
      !org.computeCurrentSlotImpactErrorable.currentSlot
    ) {
      personImpacts = event.attendees
        .map((attendee) => ({ calendarId: attendee.urnValue }))
        .filter(({ calendarId }) => calendarId.indexOf(roomString) === -1);
    } else {
      const computeCurrentSlotImpact = org.computeCurrentSlotImpactErrorable;

      const { currentSlot, uncrawledCalendars } = computeCurrentSlotImpact;

      const { summary, attendanceImpacts } = currentSlot;
      const { focusTimeMinutesCost } = summary;
      score = -focusTimeMinutesCost / 60;

      personImpacts = attendanceImpacts
        .filter((ai) => {
          // filter out rooms
          return ai.calendarId.indexOf(roomString) === -1;
        })
        .sort((a, b) => {
          const aScore = -a.focusTimeMinutesCost / 60;
          const bScore = -b.focusTimeMinutesCost / 60;
          return aScore - bScore;
        })
        .map((impacts) => ({ impacts, calendarId: impacts.calendarId }));

      personImpacts.push.apply(
        personImpacts,
        uncrawledCalendars
          .filter((calendarId) => {
            // filter out rooms
            return calendarId.indexOf(roomString) === -1;
          })
          .map((calendarId) => ({ calendarId })),
      );
    }

    const attendeeCount = personImpacts.length;

    const personMap: { [email: string]: ISchema.IPerson } = {};
    let personList: ISchema.IPerson[] = [];

    if (event && event.attendeePeople.__typename === "PersonList") {
      personList = event.attendeePeople.list;
      personList.forEach((person) => {
        personMap[person.primaryEmail] = person;
      });
    }

    const attendeeMap: Record<string, ISchema.IAttendee> = {};

    event.attendees.forEach((attendee) => (attendeeMap[attendee.urnValue] = attendee));

    personImpacts = personImpacts.sort((a, b) => {
      const aAttendee = attendeeMap[a.calendarId] || null;
      const bAttendee = attendeeMap[b.calendarId] || null;

      if (aAttendee && aAttendee.organizer) {
        return -1;
      } else if (bAttendee && bAttendee.organizer) {
        return 1;
      }

      return 0;
    });

    const maxRenderedAvatarsWithOverflow =
      maxRenderedAvatars - (attendeeCount > maxRenderedAvatars ? 1 : 0);

    const renderable = personImpacts.slice(0, maxRenderedAvatarsWithOverflow); // grab first few calIds to render

    const renderCount = renderable.length;

    const overflow = personImpacts.slice(maxRenderedAvatarsWithOverflow, 8); // render a few calendarIds in the remainder tooltip

    const bucketedAttendees = getAttendeesBucketedByResponse(event);

    const responseStatusTextMap = {
      Accepted: "attending",
      Tentative: "tentative",
      Declined: "not attending",
      NeedsAction: "awaiting",
    };

    const responseTexts: string[] = [];

    Object.keys(responseStatusTextMap).forEach(
      (responseStatus: keyof typeof responseStatusTextMap) => {
        if (!bucketedAttendees[responseStatus].length) {
          return;
        }

        responseTexts.push(
          `${bucketedAttendees[responseStatus].length} ${responseStatusTextMap[responseStatus]}`,
        );
      },
    );

    return (
      <div style={{ ...s.container, ...containerStyle }}>
        {this.renderScore(score, attendeeCount)}
        <div style={s.responseText}>{responseTexts.join(", ")}</div>
        {this.state.expanded ? null : this.renderPain(org, personList)}
        {!this.state.expanded ? (
          <div ref={this.bindProfileCostsContainer} style={s.profileCosts}>
            <div style={s.profileCostsAbsolute}>
              {renderable.map((personImpact) => {
                const person = personMap[personImpact.calendarId] || null;
                const attendee = attendeeMap[personImpact.calendarId] || null;
                return this.renderPerson(org, personImpact, person, attendee);
              })}
              {attendeeCount - renderCount > 0 ? (
                <div
                  style={{ ...s.remainder, width: 34, height: 34 }}
                  data-for={this.toolTipId}
                  data-tip
                >
                  +{attendeeCount - maxRenderedAvatarsWithOverflow}
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        ) : (
          <div style={s.rowsContainer}>
            {personImpacts.slice(0, 15).map((personImpact) => {
              const person = personMap[personImpact.calendarId] || null;
              const attendee = attendeeMap[personImpact.calendarId] || null;
              return this.renderPersonRow(org, personImpact, person, attendee);
            })}
            {personImpacts.length > 15 ? (
              <div
                style={{ ...s.remainder, ...s.rowOthers, width: 34, height: 34 }}
                data-for={this.toolTipId}
                data-tip
              >
                +{personImpacts.length - 15}
              </div>
            ) : null}
          </div>
        )}
        <div
          style={s.expandToggle}
          onClick={() => this.setState({ expanded: !this.state.expanded })}
        >
          {this.state.expanded ? "Show less" : "Show more"}
        </div>
        <ReactTooltip multiline effect="solid" type="dark" id={this.toolTipId}>
          {overflow.map((personImpact) => (
            <div key={personImpact.calendarId}>{personImpact.calendarId}</div>
          ))}
          {attendeeCount - 8 > 0 ? <div>+{attendeeCount - 8} more</div> : ""}
        </ReactTooltip>
      </div>
    );
  }
}

///////////
// Relay //
///////////

export const EventPeople = createFragmentContainer<IContainer>(
  EventPeopleCmpt,
  eventPeopleFragments,
);
